import React, { useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  IconButton,
  Tooltip,
  Dialog,
  useMediaQuery,
  Slide,
} from "@material-ui/core";
import DialpadIcon from "@material-ui/icons/Dialpad";
import PhoneIcon from "@material-ui/icons/Phone";
import BackspaceIcon from "@material-ui/icons/Backspace";
import CloseIcon from "@material-ui/icons/Close";
import { green } from "@material-ui/core/colors";
import { useVoice } from "../../context/Voice/VoiceContext";
import { useSoftphone } from "../../context/Voice/SoftphoneContext";

/**
 * SoftphoneDialer — an Apple-style keypad for placing a call to ANY number,
 * presented in a translucent modal in the site's green brand. Talking is done
 * FROM the system (softphone, device mic); the customer receives a direct call.
 *
 * Scoped strictly to the dialer surface — touches NO existing app chrome. Lives
 * in the composer (bottom) bar next to the quick-call button. Responsive: a
 * bottom-sheet on phones, a centred rounded modal on desktop/tablet.
 */
const BRAND = "#25d366";
const BRAND_DARK = "#1faa52";

const KEYS = [
  { d: "1", s: "" },
  { d: "2", s: "ABC" },
  { d: "3", s: "DEF" },
  { d: "4", s: "GHI" },
  { d: "5", s: "JKL" },
  { d: "6", s: "MNO" },
  { d: "7", s: "PQRS" },
  { d: "8", s: "TUV" },
  { d: "9", s: "WXYZ" },
  { d: "*", s: "" },
  { d: "0", s: "+" },
  { d: "#", s: "" },
];

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    paper: {
      // Translucent "frosted glass" surface — the brand wash sits over a blur.
      background: dark
        ? "linear-gradient(180deg, rgba(20,28,24,0.92), rgba(12,18,15,0.94))"
        : "linear-gradient(180deg, rgba(255,255,255,0.90), rgba(244,250,246,0.94))",
      backdropFilter: "blur(22px) saturate(140%)",
      WebkitBackdropFilter: "blur(22px) saturate(140%)",
      border: `1px solid ${dark ? "rgba(46,227,122,0.18)" : "rgba(37,211,102,0.22)"}`,
      borderRadius: 26,
      boxShadow: "0 24px 70px rgba(0,0,0,0.35)",
      overflow: "hidden",
      width: 360,
      maxWidth: "92vw",
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        maxWidth: "100%",
        borderRadius: "26px 26px 0 0",
        margin: 0,
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
      },
    },
    wrap: { direction: "rtl", padding: "18px 22px 24px", display: "flex", flexDirection: "column", alignItems: "center" },
    head: { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 },
    title: { fontSize: 13, fontWeight: 700, color: dark ? "rgba(230,247,236,0.7)" : "#5f6f6a", letterSpacing: 0.3 },
    close: { color: dark ? "rgba(230,247,236,0.6)" : "#90a29b" },
    display: {
      direction: "ltr",
      minHeight: 52,
      width: "100%",
      textAlign: "center",
      fontSize: 34,
      fontWeight: 500,
      letterSpacing: 1.5,
      color: dark ? "#eafff2" : "#0B0D0C",
      fontVariantNumeric: "tabular-nums",
      padding: "6px 0 2px",
      wordBreak: "break-all",
      lineHeight: 1.15,
    },
    placeholder: { color: dark ? "rgba(230,247,236,0.28)" : "rgba(11,13,12,0.25)", fontSize: 20, fontWeight: 400 },
    pad: {
      // Fixed-size circular keys in a centred grid — avoids CSS `aspect-ratio`
      // (unsupported on iOS Safari < 15 / older Android & desktop browsers, where
      // it would collapse the keys). This renders identically everywhere.
      display: "grid",
      gridTemplateColumns: "repeat(3, 72px)",
      justifyContent: "center",
      gap: 16,
      margin: "14px 0 6px",
      width: "100%",
    },
    key: {
      width: 72,
      height: 72,
      borderRadius: "50%",
      border: "none",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: dark ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.045)",
      transition: "transform .06s ease, background .12s ease",
      outline: "none",
      userSelect: "none",
      "@media (hover:hover)": {
        "&:hover": { background: dark ? "rgba(46,227,122,0.14)" : "rgba(37,211,102,0.12)" },
      },
      "&:active": { transform: "scale(0.92)", background: dark ? "rgba(46,227,122,0.2)" : "rgba(37,211,102,0.18)" },
    },
    keyD: { fontSize: 28, fontWeight: 500, lineHeight: 1, color: dark ? "#eafff2" : "#0B0D0C" },
    keyS: { fontSize: 9.5, fontWeight: 700, letterSpacing: 1.5, marginTop: 3, color: dark ? "rgba(230,247,236,0.5)" : "#8a9a93", minHeight: 11 },
    actions: { display: "flex", alignItems: "center", justifyContent: "center", gap: 28, width: "100%", marginTop: 8 },
    callBtn: {
      width: 66,
      height: 66,
      borderRadius: "50%",
      border: "none",
      cursor: "pointer",
      background: `linear-gradient(180deg, ${green[400]}, ${BRAND_DARK})`,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: `0 8px 22px ${dark ? "rgba(37,211,102,0.4)" : "rgba(37,211,102,0.45)"}`,
      transition: "transform .08s ease, box-shadow .15s ease, opacity .15s ease",
      "&:active": { transform: "scale(0.93)" },
      "&:disabled": { opacity: 0.4, cursor: "not-allowed", boxShadow: "none" },
    },
    back: {
      width: 52,
      height: 52,
      visibility: "hidden", // keeps the call button centred; shown when there are digits
      color: dark ? "rgba(230,247,236,0.7)" : "#5f6f6a",
    },
    backShown: { visibility: "visible" },
  };
});

const SoftphoneDialer = ({ ticket, iconClassName }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreenSheet = useMediaQuery(theme.breakpoints.down("xs"));
  const { status, activeCall, startCall } = useVoice();
  const softphone = useSoftphone() || {};
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");

  if (!status || !status.enabled || !status.outgoingEnabled) return null;

  const softphoneMode = Boolean(status.softphoneAvailable) && softphone.supported === true;

  const press = (d) => setNumber((n) => (n.length < 20 ? n + d : n));
  const backspace = () => setNumber((n) => n.slice(0, -1));

  const dial = () => {
    const n = number.trim();
    if (!n || activeCall) return;
    startCall({
      toNumber: n,
      contactId: ticket && (ticket.contactId || (ticket.contact && ticket.contact.id)),
      ticketId: ticket && ticket.id,
      purpose: "",
      mode: softphoneMode ? "softphone" : "direct",
    });
    setNumber("");
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="חייגן — חיוג למספר">
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          aria-label="חייגן"
          style={{ padding: 6 }}
        >
          <DialpadIcon
            fontSize="small"
            className={iconClassName}
            style={iconClassName ? { color: BRAND } : { color: green[600] }}
          />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        classes={{ paper: classes.paper }}
        BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" } }}
        style={fullScreenSheet ? { alignItems: "flex-end" } : undefined}
      >
        <div className={classes.wrap}>
          <div className={classes.head}>
            <span className={classes.title}>חיוג מהמערכת {softphoneMode ? "· 💻 דיבור מהמכשיר" : "· 📞 ישיר"}</span>
            <IconButton size="small" className={classes.close} onClick={() => setOpen(false)} aria-label="סגור">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div className={classes.display}>
            {number || <span className={classes.placeholder}>הקלד מספר</span>}
          </div>

          <div className={classes.pad}>
            {KEYS.map((k) => (
              <button
                key={k.d}
                type="button"
                className={classes.key}
                onClick={() => press(k.d)}
                aria-label={k.d}
              >
                <span className={classes.keyD}>{k.d}</span>
                <span className={classes.keyS}>{k.s}</span>
              </button>
            ))}
          </div>

          <div className={classes.actions}>
            <span style={{ width: 52 }} />
            <button
              type="button"
              className={classes.callBtn}
              onClick={dial}
              disabled={!number.trim() || Boolean(activeCall)}
              aria-label="חייג"
            >
              <PhoneIcon style={{ fontSize: 30 }} />
            </button>
            <IconButton
              className={`${classes.back} ${number ? classes.backShown : ""}`}
              onClick={backspace}
              aria-label="מחק"
            >
              <BackspaceIcon />
            </IconButton>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default SoftphoneDialer;
